import type { ReactNode } from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  Box,
  Group,
  ActionIcon,
  Title,
  ScrollArea,
  Button,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { ArrowLeft } from "@phosphor-icons/react";

interface DocumentModalProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  content: ReactNode;
  onAgree: () => void;
  zIndex?: number;
}

export function DocumentModal({
  opened,
  onClose,
  title,
  content,
  onAgree,
  zIndex = 200,
}: DocumentModalProps) {
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (opened) {
      setHasScrolledToBottom(false);
    }
  }, [opened]);

  const checkScrollPosition = useCallback(() => {
    let scrollElement: HTMLElement | null = viewportRef.current;
    
    if (!scrollElement || scrollElement.scrollHeight === 0) {
      if (scrollAreaRef.current) {
        const scrollableDiv = scrollAreaRef.current.querySelector('[data-scrollable]') as HTMLElement | null;
        if (scrollableDiv) {
          scrollElement = scrollableDiv;
        } else {
          const allDivs = scrollAreaRef.current.querySelectorAll('div');
          for (const div of allDivs) {
            if (div.scrollHeight > div.clientHeight) {
              scrollElement = div as HTMLElement;
              break;
            }
          }
        }
      }
    }

    if (scrollElement) {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
      setHasScrolledToBottom(isAtBottom);
    }
  }, []);

  const handleAgree = useCallback(() => {
    const scrollElement = viewportRef.current;
    if (scrollElement) {
      const scrollTop = scrollElement.scrollTop;
      requestAnimationFrame(() => {
        if (scrollElement && scrollElement.scrollTop !== scrollTop) {
          scrollElement.scrollTop = scrollTop;
        }
        onAgree();
      });
    } else {
      onAgree();
    }
  }, [onAgree]);

  useEffect(() => {
    if (opened) {
      const findAndAttachScrollListener = (): (() => void) | undefined => {
        let scrollElement: HTMLElement | null = viewportRef.current;
        
        if (!scrollElement || scrollElement.scrollHeight === 0) {
          if (scrollAreaRef.current) {
            const viewport = scrollAreaRef.current.querySelector('div[data-scrollable]') as HTMLElement | null;
            if (viewport && viewport.scrollHeight > viewport.clientHeight) {
              scrollElement = viewport;
            } else {
              const allDivs = scrollAreaRef.current.querySelectorAll('div');
              for (const div of allDivs) {
                if (div.scrollHeight > div.clientHeight && div.scrollTop !== undefined) {
                  scrollElement = div as HTMLElement;
                  break;
                }
              }
            }
          }
        }

        if (scrollElement && scrollElement.scrollHeight > 0) {
          scrollElement.addEventListener("scroll", checkScrollPosition, { passive: true });
          checkScrollPosition();
          
          return () => {
            scrollElement?.removeEventListener("scroll", checkScrollPosition);
          };
        }
        return undefined;
      };

      let cleanup: (() => void) | undefined = findAndAttachScrollListener();
      
      const rafId = requestAnimationFrame(() => {
        cleanup?.();
        cleanup = findAndAttachScrollListener();
      });
      
      const timeoutId1 = setTimeout(() => {
        cleanup?.();
        cleanup = findAndAttachScrollListener();
      }, 100);
      
      const timeoutId2 = setTimeout(() => {
        checkScrollPosition();
      }, 300);
      
      const timeoutId3 = setTimeout(() => {
        checkScrollPosition();
      }, 500);

      return () => {
        cleanup?.();
        cancelAnimationFrame(rafId);
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
      };
    }
  }, [opened, checkScrollPosition]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      centered
      size="xl"
      zIndex={zIndex}
      lockScroll={false}
      overlayProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
      styles={{
        body: {
          overflowX: "hidden",
          maxWidth: "100%",
          wordWrap: "break-word",
          padding: "24px",
        },
        content: {
          overflowX: "hidden",
          maxWidth: "900px",
          wordWrap: "break-word",
          backgroundColor: colorScheme === "dark" ? theme.colors.dark[6] : "white",
        },
        header: {
          overflowX: "hidden",
          maxWidth: "100%",
          wordWrap: "break-word",
          backgroundColor: colorScheme === "dark" ? theme.colors.dark[6] : "white",
        },
      }}
    >
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          height: "calc(90vh - 100px)",
          overflowX: "hidden",
          maxWidth: "100%",
          wordWrap: "break-word",
          position: "relative",
        }}
      >
        <Group justify="space-between" align="center" mb="md" style={{ position: "relative" }}>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={onClose}
            aria-label="Back"
            styles={{
              root: {
                position: "absolute",
                left: 0,
                zIndex: 10,
                border: "none",
                outline: "none",
                boxShadow: "none",
              },
            }}
          >
            <ArrowLeft size={18} />
          </ActionIcon>
          <Title
            order={2}
            style={{
              fontFamily: "SF Pro",
              fontWeight: 510,
              fontSize: 20,
              lineHeight: "1.2em",
              textAlign: "center",
              color: colorScheme === "dark" ? "white" : "#124B8C",
              flexGrow: 1,
              width: "100%",
            }}
          >
            {title}
          </Title>
          <Box style={{ width: 40 }} />
        </Group>

        <ScrollArea
          ref={scrollAreaRef}
          style={{ 
            flex: 1, 
            paddingRight: "16px",
            minHeight: 0,
            position: "relative",
          }}
          viewportRef={viewportRef}
        >
          {content}
        </ScrollArea>

        <Box 
          mt="md" 
          style={{ 
            display: "flex", 
            justifyContent: "flex-start",
            minHeight: "48px",
            flexShrink: 0,
          }}
        >
          <Button
            size="md"
            onClick={handleAgree}
            disabled={!hasScrolledToBottom}
            style={{
              backgroundColor: hasScrolledToBottom ? "#124B8C" : "#CCCCCC",
              color: "white",
              borderRadius: "8px",
              padding: "7px 32px",
              fontWeight: 500,
              fontSize: "16px",
              cursor: hasScrolledToBottom ? "pointer" : "not-allowed",
              transition: "background-color 0.2s ease",
              minWidth: "120px",
            }}
          >
            I Agree
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

